import React, { useState } from 'react';
import { Modal, Icon, Alert, Upload, Button, Table, message, Spin, Tooltip, Tag } from 'antd';
import readXlsxFile from 'read-excel-file';
import { ValidateImportReservation, CreateImportReservation } from 'utils/apis/reservation';
import { QuestionCircleOutlined } from '@ant-design/icons';

const { Dragger } = Upload;

// Excel sheet structure constants
const HEADER_ROW_INDEX = 1;
const DATA_START_ROW_INDEX = 2;

const ImportReservation = ({ onCancel, visible, ...props }) => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [parsedData, setParsedData] = useState([]);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [validationError, setValidationError] = useState(false);
  // Static table columns definition
  const staticColumns = [
    {
      title: 'Booking Type',
      dataIndex: 'bookingType',
      key: 'bookingType',
      width: 120
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (value, record) => {
        switch (value) {
          case 'ok':
            return <Tag color="green">OK</Tag>;
          case 'error':
            return (
              <Tooltip title={record.errorMessage}>
                <Tag color="red">
                  <QuestionCircleOutlined /> Error
                </Tag>
              </Tooltip>
            );
          default:
            return <Tag color="default">{value}</Tag>;
        }
      }
    },
    {
      title: 'Property Name',
      dataIndex: 'propertyName',
      key: 'propertyName',
      width: 150
    },
    {
      title: 'Room Type',
      dataIndex: 'roomType',
      key: 'roomType',
      width: 120
    },
    {
      title: 'Unit Name',
      dataIndex: 'unitName',
      key: 'unitName',
      width: 120
    },
    {
      title: 'Check In Date',
      dataIndex: 'checkInDate',
      key: 'checkInDate',
      width: 120,
      render: value => {
        if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
          return value;
        }
        return value ? new Date(value).toLocaleDateString() : '-';
      }
    },
    {
      title: 'Check Out Date',
      dataIndex: 'checkOutDate',
      key: 'checkOutDate',
      width: 120,
      render: value => {
        if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
          return value;
        }
        return value ? new Date(value).toLocaleDateString() : '-';
      }
    },
    {
      title: 'Confirmation Code',
      dataIndex: 'confirmationCode',
      key: 'confirmationCode',
      width: 150
    },
    {
      title: 'Booking Source',
      dataIndex: 'bookingSource',
      key: 'bookingSource',
      width: 120
    },
    {
      title: 'Booking Status',
      dataIndex: 'bookingStatus',
      key: 'bookingStatus',
      width: 120
    },
    {
      title: 'Number of pax',
      dataIndex: 'numberOfPax',
      key: 'numberOfPax',
      width: 100,
      render: value => value || '-'
    },
    {
      title: 'Remarks',
      dataIndex: 'remarks',
      key: 'remarks',
      width: 150
    },
    {
      title: 'Room Rate',
      dataIndex: 'roomRate',
      key: 'roomRate',
      width: 100,
      render: value => (value ? value.toLocaleString() : '-')
    },
    {
      title: 'Platform Fee',
      dataIndex: 'platformFee',
      key: 'platformFee',
      width: 100,
      render: value => (value ? value.toLocaleString() : '-')
    },
    {
      title: 'Total Price',
      dataIndex: 'totalPrice',
      key: 'totalPrice',
      width: 100,
      render: value => (value ? value.toLocaleString() : '-')
    },
    {
      title: 'Sales Service Tax',
      dataIndex: 'salesServiceTax',
      key: 'salesServiceTax',
      width: 120,
      render: value => (value ? value.toLocaleString() : '-')
    },
    {
      title: 'Sales Tax',
      dataIndex: 'salesTax',
      key: 'salesTax',
      width: 100,
      render: value => (value ? value.toLocaleString() : '-')
    },
    {
      title: 'Tourism Tax',
      dataIndex: 'tourismTax',
      key: 'tourismTax',
      width: 100,
      render: value => (value ? value.toLocaleString() : '-')
    },
    {
      title: 'Heritage Tax',
      dataIndex: 'heritageTax',
      key: 'heritageTax',
      width: 100,
      render: value => (value ? value.toLocaleString() : '-')
    },
    {
      title: 'OTA Tax',
      dataIndex: 'otaTax',
      key: 'otaTax',
      width: 100,
      render: value => (value ? value.toLocaleString() : '-')
    },
    {
      title: 'Guest First Name',
      dataIndex: 'guestFirstName',
      key: 'guestFirstName',
      width: 120
    },
    {
      title: 'Guest Last Name',
      dataIndex: 'guestLastName',
      key: 'guestLastName',
      width: 120
    },
    {
      title: 'Gross Amount',
      dataIndex: 'grossAmount',
      key: 'grossAmount',
      width: 120,
      render: value => (value ? value.toLocaleString() : '-')
    }
  ];

  // Define schema for reservation data
  const reservationSchema = {
    'Booking Type': {
      prop: 'bookingType',
      type: String,
      required: true
    },
    'Property Name': {
      prop: 'propertyName',
      type: String,
      required: true
    },
    'Room Type': {
      prop: 'roomType',
      type: String,
      required: true
    },
    'Unit Name': {
      prop: 'unitName',
      type: String,
      required: true
    },
    'Check In Date': {
      prop: 'checkInDate',
      type: Date,
      required: true
    },
    'Check Out Date': {
      prop: 'checkOutDate',
      type: Date,
      required: true
    },
    'Confirmation Code': {
      prop: 'confirmationCode',
      type: String,
      required: true
    },
    'Booking Source': {
      prop: 'bookingSource',
      type: String,
      required: true
    },
    'Booking Status': {
      prop: 'bookingStatus',
      type: String,
      required: true
    },
    'Number of pax': {
      prop: 'numberOfPax',
      type: Number,
      required: false
    },
    Remarks: {
      prop: 'remarks',
      type: String,
      required: false
    },
    'Room Rate': {
      prop: 'roomRate',
      type: Number,
      required: false
    },
    'Platform Fee': {
      prop: 'platformFee',
      type: Number,
      required: true
    },
    'Total Price': {
      prop: 'totalPrice',
      type: Number,
      required: false
    },
    'Sales Service Tax': {
      prop: 'salesServiceTax',
      type: Number,
      required: false
    },
    'Sales Tax': {
      prop: 'salesTax',
      type: Number,
      required: false
    },
    'Tourism Tax': {
      prop: 'tourismTax',
      type: Number,
      required: false
    },
    'Heritage Tax': {
      prop: 'heritageTax',
      type: Number,
      required: false
    },
    'OTA Tax': {
      prop: 'otaTax',
      type: Number,
      required: false
    },
    'Guest First Name': {
      prop: 'guestFirstName',
      type: String,
      required: true
    },
    'Guest Last Name': {
      prop: 'guestLastName',
      type: String,
      required: true
    },
    'Gross Amount': {
      prop: 'grossAmount',
      type: Number,
      required: false
    }
  };

  // Helper function to convert Excel date serial number to YYYY-MM-DD format
  const convertExcelDate = excelDate => {
    try {
      // Excel date serial number (days since 1900-01-01, with 1900 incorrectly treated as leap year)
      // We need to adjust for Excel's date system
      const excelEpoch = new Date(1900, 0, 1); // January 1, 1900
      const daysSinceEpoch = Number(excelDate) - 2; // Subtract 2 to account for Excel's leap year bug
      const date = new Date(excelEpoch.getTime() + daysSinceEpoch * 24 * 60 * 60 * 1000);

      // Format as YYYY-MM-DD
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');

      return `${year}-${month}-${day}`;
    } catch (error) {
      console.error('Error converting Excel date:', error);
      return null;
    }
  };

  // Helper function to detect empty Excel file
  const isExcelEmpty = data => {
    if (!data || data.length === 0) {
      return true;
    }

    // Check if all rows are empty (no data beyond headers)
    const hasData = data.some(row => {
      if (!row || row.length === 0) return false;
      return row.some(cell => cell !== null && cell !== undefined && cell !== '');
    });

    return !hasData;
  };

  // Helper function to validate schema matching
  const validateSchemaMatch = headers => {
    const expectedHeaders = Object.keys(reservationSchema);
    const missingHeaders = [];
    const extraHeaders = [];

    // Check for missing required headers
    expectedHeaders.forEach(expectedHeader => {
      if (!headers.includes(expectedHeader)) {
        missingHeaders.push(expectedHeader);
      }
    });

    // Check for extra headers not in schema
    headers.forEach(header => {
      if (!expectedHeaders.includes(header)) {
        extraHeaders.push(header);
      }
    });

    return {
      isValid: missingHeaders.length === 0,
      missingHeaders,
      extraHeaders
    };
  };

  // Helper function to process data row without validation (validation will be done at backend)
  const processDataRow = (row, headers) => {
    const rowData = {};

    headers.forEach((header, index) => {
      const cellValue = row[index];
      const schemaField = reservationSchema[header];

      if (schemaField) {
        let value = cellValue;
        // Convert data types for better API submission
        if (schemaField.type === String && value !== null && value !== undefined && value !== '') {
          // Ensure string values remain as strings, preserving format (e.g., "001" stays "001")
          value = String(value);
        } else if (schemaField.type === Number && value !== null && value !== undefined && value !== '') {
          value = Number(value);
        } else if (schemaField.type === Date && value !== null && value !== undefined && value !== '') {
          // Check if it's already a Date object (from Excel parser)
          if (value instanceof Date) {
            // Format Date object to YYYY-MM-DD format
            const year = value.getFullYear();
            const month = String(value.getMonth() + 1).padStart(2, '0');
            const day = String(value.getDate()).padStart(2, '0');
            value = `${year}-${month}-${day}`;
          } else if (typeof value === 'number') {
            // It's an Excel serial number (numeric)
            value = convertExcelDate(value);
          } else if (typeof value === 'string' && !isNaN(Number(value)) && value.trim().match(/^\d+(\.\d+)?$/)) {
            // It's an Excel serial number as a string (e.g., "45000")
            value = convertExcelDate(Number(value));
          } else {
            // It's a date string, parse and format it
            const date = new Date(value);
            if (!isNaN(date.getTime())) {
              const year = date.getFullYear();
              const month = String(date.getMonth() + 1).padStart(2, '0');
              const day = String(date.getDate()).padStart(2, '0');
              value = `${year}-${month}-${day}`;
            } else {
              value = null;
            }
          }
        }

        if (schemaField.prop === 'confirmationCode') {
          value = String(value);
        }
        rowData[schemaField.prop] = value;
      }
    });

    return rowData;
  };

  const handleFileUpload = async file => {
    setLoading(true);
    try {
      // 1. Parse Excel file
      const output = await readXlsxFile(file);

      // 2. Check if Excel file is empty
      if (isExcelEmpty(output)) {
        message.error('Excel file is empty or contains no data');
        setLoading(false);
        return;
      }

      // 3. Extract headers and data
      const headers = output[HEADER_ROW_INDEX] || []; // Header row
      const dataRows = output.slice(DATA_START_ROW_INDEX); // Data rows

      // 4. Validate schema matching
      const schemaValidation = validateSchemaMatch(headers);
      if (!schemaValidation.isValid) {
        let errorMessage = 'Excel file headers do not match the required schema.\n';
        if (schemaValidation.missingHeaders.length > 0) {
          errorMessage += `Missing required headers: ${schemaValidation.missingHeaders.join(', ')}\n`;
        }
        if (schemaValidation.extraHeaders.length > 0) {
          errorMessage += `Extra headers found: ${schemaValidation.extraHeaders.join(', ')}\n`;
        }
        errorMessage += 'Please download the template and use the correct format.';
        message.error(errorMessage);
        setLoading(false);
        return;
      }

      console.log(dataRows, 445);
      // 5. Process data rows into array for API submission
      const processedData = dataRows.map((row, index) => {
        const rowData = processDataRow(row, headers);
        return { ...rowData, key: index };
      });

      console.log(processedData, 453);
      // Set parsed data
      setParsedData(processedData);
      setUploadedFile(file);

      // 6. Call validate API
      message.loading('Validating reservations with backend...', 0);
      try {
        const apiData = processedData.map(item => {
          const { key, ...data } = item;
          return data;
        });

        const validationResponse = await ValidateImportReservation({
          reservations: apiData
        });

        console.log(validationResponse, 467);
        if (validationResponse.data.status !== 'ok') {
          message.destroy();
          message.error('Validation failed. Please try again.');
          setStep(2); // Go to combined preview/validation step
          return;
        }

        setParsedData(validationResponse.data.reservations);
        message.destroy(); // Clear loading message

        const isError = validationResponse.data.reservations.filter(item => item.status === 'error');

        if (isError.length === 0) {
          setValidationError(false);
          message.success(`Validation successful! ${processedData.length} reservations are ready for import.`);
          setStep(2); // Go to combined preview/validation step
        } else {
          setValidationError(true);
          message.error(
            `Validation completed with ${(validationResponse.data.errors && validationResponse.data.errors.length) ||
              0} errors. Please review and fix.`
          );
          setStep(2); // Go to combined preview/validation step to show errors
        }
      } catch (validationError) {
        message.destroy(); // Clear loading message
        console.error('Validation API error:', validationError);
        message.error('Failed to validate reservations. Please try again.');
      }
    } catch (error) {
      console.error('Error parsing Excel file:', error);
      message.error('Failed to parse Excel file. Please check the file format and try again.');
    } finally {
      setLoading(false);
    }
  };

  // Helper function to prepare data for API submission
  const prepareDataForAPI = () => {
    return parsedData.map(item => {
      // Remove the key property that was added for React rendering
      const { key, ...apiData } = item;
      return apiData;
    });
  };

  const handleNext = async () => {
    if (step === 1 && parsedData.length > 0) {
      // File upload and validation is handled in handleFileUpload
      // This will automatically move to step 2 after validation
    } else if (step === 2) {
      // Import the validated reservations
      if (validationError === false) {
        setLoading(true);
        try {
          const apiData = prepareDataForAPI();
          message.loading('Importing reservations...', 0);

          const importResponse = await CreateImportReservation({
            reservations: apiData
          });

          message.destroy(); // Clear loading message

          if (importResponse && importResponse.data) {
            setImportResult(importResponse.data);
            message.success(`Successfully imported ${apiData.length} reservations!`);
            setStep(3); // Go to import results step
          } else {
            message.error('Import failed. Please try again.');
          }
        } catch (importError) {
          message.destroy(); // Clear loading message
          console.error('Import API error:', importError);
          message.error('Failed to import reservations. Please try again.');
        } finally {
          setLoading(false);
        }
      } else {
        message.error('Cannot import reservations with validation errors. Please fix the errors first.');
      }
    }
  };

  const handleBack = () => {
    if (step === 2) {
      // Reset upload state when going back to step 1
      setParsedData([]);
      setUploadedFile(null);
      setValidationError(false);
      setStep(1);
    } else if (step === 3) {
      setStep(2);
    }
  };

  const resetUpload = () => {
    setParsedData([]);
    setUploadedFile(null);
    setImportResult(null);
    setStep(1);
  };

  const renderStep1 = () => (
    <>
      <Alert
        message="Advice for Bulk Upload Reservations"
        description={
          <div>
            Please upload the reservations in Excel format. Maximum 10 files per upload. File size must be less than 10MB. Strictly prohibited from
            uploading company data or other banned files.
            <br />
            <a href="/template/Import-Reservation-Template.xlsx" target="_blank">
              Download Excel Template
            </a>
          </div>
        }
        type="info"
        showIcon
        style={{ marginBottom: 24 }}
      />
      <Dragger
        beforeUpload={() => false}
        onChange={info => {
          if (info.fileList.length > 0) {
            handleFileUpload(info.fileList[0].originFileObj);
          }
        }}
        accept=".xlsx,.xls"
        fileList={uploadedFile ? [uploadedFile] : []}
      >
        <p className="ant-upload-drag-icon">
          <Icon type="inbox" />
        </p>
        <p className="ant-upload-text">Click or drag file to this area to upload</p>
        <p className="ant-upload-hint">Support for Excel files (.xlsx, .xls) only.</p>
      </Dragger>
    </>
  );

  const renderStep2 = () => (
    <>
      <Alert
        message="Data Preview & Validation Results"
        description={
          validationError === false
            ? `Successfully parsed and validated ${parsedData.length} reservations. All reservations are ready for import.`
            : (() => {
                const errorCount = parsedData.filter(item => item && item.status === 'error').length;
                return `Successfully parsed ${parsedData.length} reservations. Validation completed with ${errorCount} errors. Please review the issues below.`;
              })()
        }
        type={validationError === false ? 'success' : 'warning'}
        showIcon
        style={{ marginBottom: 24 }}
      />

      <Table
        columns={staticColumns}
        dataSource={parsedData}
        rowKey={(record, index) => index}
        scroll={{ x: true }}
        size="small"
        pagination={{ pageSize: 10 }}
      />
    </>
  );

  const renderStep3 = () => (
    <>
      <Alert
        message="Import Completed"
        description={`Successfully imported ${parsedData.length} reservations!`}
        type="success"
        showIcon
        style={{ marginBottom: 24 }}
      />

      {importResult && (
        <div style={{ marginBottom: 16 }}>
          <h4>Import Summary:</h4>
          <p>Total processed: {importResult.totalProcessed || parsedData.length}</p>
          <p>Successfully imported: {importResult.successCount || parsedData.length}</p>
          {importResult.errorCount > 0 && <p style={{ color: 'red' }}>Failed: {importResult.errorCount}</p>}
        </div>
      )}
    </>
  );

  return (
    <Modal
      title={`Import Reservation - Step ${step}`}
      visible={visible}
      onCancel={onCancel}
      width={1000}
      footer={
        <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
          <div>
            <Button onClick={onCancel}>Cancel</Button>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            {step > 1 && <Button onClick={handleBack}>Back</Button>}
            {(step === 2 || step === 3) && <Button onClick={resetUpload}>{step === 3 ? 'Import New File' : 'Upload New File'}</Button>}
            {step === 1 && (
              <Button type="primary" onClick={handleNext} disabled={parsedData.length === 0}>
                Next
              </Button>
            )}
            {step === 2 && (
              <Button type="primary" onClick={handleNext} disabled={validationError === true}>
                Import Reservations
              </Button>
            )}
            {step === 3 && (
              <Button type="primary" onClick={onCancel}>
                Close
              </Button>
            )}
          </div>
        </div>
      }
    >
      <Spin spinning={loading}>
        {step === 1 && renderStep1()}
        {step === 2 && renderStep2()}
        {step === 3 && renderStep3()}
      </Spin>
    </Modal>
  );
};

export default ImportReservation;
